import { useCallback, useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import {
  api,
  getRemoteSession,
  type GameDetail,
  type ProjectedGameState,
} from '../api';
import { AppShell } from '../components/AppShell';
import { serverNow } from '../time-sync';

export function GameControlPage() {
  const { id } = useParams<{ id: string }>();
  const [game, setGame] = useState<GameDetail | null>(null);
  const [state, setState] = useState<ProjectedGameState | null>(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!id) return;
    try {
      setErr(null);
      const g = await api.getGame(id);
      setGame(g);
      setState(g.currentState ?? null);
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'failed_to_load');
    }
  }, [id]);

  useEffect(() => {
    load();
  }, [load]);

  async function fire(eventType: string, payload?: Record<string, unknown>) {
    if (!id) return;
    setBusy(true);
    try {
      const res = await api.appendEvent(id, { eventType, payload });
      setState(res.state);
    } finally {
      setBusy(false);
    }
  }

  async function undo() {
    if (!id) return;
    setBusy(true);
    try {
      const res = await api.undoLast(id);
      setState(res.state);
    } catch {
      // ignore — no events to undo
    } finally {
      setBusy(false);
    }
  }

  async function start() {
    if (!id) return;
    setBusy(true);
    try {
      await api.startGame(id);
      await load();
    } finally {
      setBusy(false);
    }
  }

  async function end() {
    if (!id) return;
    if (!confirm('End this game? It will be marked FINAL.')) return;
    setBusy(true);
    try {
      await api.endGame(id);
      await load();
    } finally {
      setBusy(false);
    }
  }

  if (err) {
    return (
      <AppShell>
        <div className="card text-red-300">{err}</div>
      </AppShell>
    );
  }
  if (!game || !state) {
    return (
      <AppShell>
        <div className="text-muted">Loading…</div>
      </AppShell>
    );
  }

  const isLive = game.status === 'LIVE';
  const isOver = game.status === 'FINAL' || game.status === 'CANCELLED';
  const disabled = busy || isOver;

  // Completed games render as a read-only box score instead of the
  // operator console. Same URL, same data — different UI based on
  // status so a finished game still has a useful page.
  if (isOver) {
    return <BoxScoreView game={game} state={state} />;
  }

  return (
    <AppShell>
      <div className="space-y-2">
        <div className="flex items-center justify-between gap-2">
          <div className="flex min-w-0 items-center gap-3">
            {!getRemoteSession() && (
              <Link to="/games" className="text-sm text-muted hover:text-text">
                ←
              </Link>
            )}
            <h1 className="truncate text-lg font-bold tracking-tight">
              {game.homeTeam.name} vs {game.awayTeam.name}
            </h1>
          </div>
          <div className="flex shrink-0 gap-2">
            {!isLive && !isOver && (
              <button className="btn btn-primary" onClick={start} disabled={busy}>
                Start game
              </button>
            )}
            {isLive && (
              <button className="btn btn-secondary" onClick={end} disabled={busy}>
                End game
              </button>
            )}
            <button
              className="btn btn-ghost"
              onClick={undo}
              disabled={busy || isOver}
            >
              ↶ Undo
            </button>
          </div>
        </div>

        {/* Compact control strip — side by side instead of stacked */}
        <div className="grid grid-cols-1 gap-2 lg:grid-cols-2">
          <ClockControl state={state} fire={fire} disabled={disabled} />
          {game.sport.slug === 'basketball' && (
            <ShotClockControl state={state} fire={fire} disabled={disabled} />
          )}
          {game.sport.slug === 'football' && (
            <>
              <PlayClockControl state={state} fire={fire} disabled={disabled} />
              <DownDistanceControl state={state} fire={fire} disabled={disabled} />
            </>
          )}
          {game.sport.slug === 'soccer' && (
            <StoppageControl state={state} fire={fire} disabled={disabled} />
          )}
          {/* Football keeps the standalone possession control; basketball's
              possession lives inside each team card (below). */}
          {game.sport.slug === 'football' && (
            <PossessionControl state={state} fire={fire} disabled={disabled} />
          )}
        </div>

        {(() => {
          const renderPanel = (sideKey: 'home' | 'away') => {
            const team = sideKey === 'home' ? game.homeTeam : game.awayTeam;
            const sideState = state[sideKey];
            return (
              <ScoringPanel
                key={sideKey}
                label={sideKey.toUpperCase()}
                team={team}
                side={sideKey}
                score={sideState.score}
                fouls={sideState.fouls}
                timeouts={sideState.timeoutsRemaining}
                bonus={sideState.bonus}
                doubleBonus={sideState.doubleBonus ?? false}
                playerStats={state.playerStats}
                playerStints={state.playerStints}
                foulOutPlayers={state.foulOutPlayers ?? []}
                sportSlug={game.sport.slug}
                onCourt={state.onCourt?.[sideKey] ?? []}
                lineupSize={lineupSizeFor(game.sport.slug)}
                // Possession toggle inside the card (basketball only).
                showPossession={game.sport.slug === 'basketball'}
                hasPossession={state.possession === sideKey}
                onPossession={() => fire('POSSESSION', { side: sideKey })}
                onLineupSet={(playerIds) =>
                  fire('LINEUP_SET', { side: sideKey, playerIds })
                }
                onSubstitute={(inPlayerId, outPlayerId) =>
                  fire('SUBSTITUTION', {
                    side: sideKey,
                    inPlayerId,
                    outPlayerId,
                  })
                }
                scoringActions={scoringActionsFor(game.sport.slug)}
                secondaryActions={secondaryActionsFor(game.sport.slug)}
                showFoulButton={game.sport.slug !== 'soccer'}
                showTimeoutButton={game.sport.slug !== 'soccer'}
                onScore={(pts, type, playerId) =>
                  fire(type, {
                    side: sideKey,
                    ...(playerId ? { playerId } : {}),
                    ...(type === 'SCORE_ADJUST' ? { delta: pts } : {}),
                  })
                }
                onFoul={(playerId, foulType) =>
                  fire('FOUL', {
                    side: sideKey,
                    ...(playerId ? { playerId } : {}),
                    ...(foulType ? { type: foulType } : {}),
                  })
                }
                onTimeout={() => fire('TIMEOUT', { side: sideKey })}
                onSecondary={(action, playerId) =>
                  fire(action.eventType, {
                    side: sideKey,
                    ...(action.payload ?? {}),
                    ...(playerId ? { playerId } : {}),
                  })
                }
                disabled={disabled}
              />
            );
          };

          // Basketball: HOME | Game Events (vertical) | AWAY.
          if (game.sport.slug === 'basketball') {
            return (
              <div className="grid grid-cols-1 gap-2 lg:grid-cols-[1fr_auto_1fr]">
                {renderPanel('home')}
                <GameEventsColumn fire={fire} disabled={disabled} />
                {renderPanel('away')}
              </div>
            );
          }
          // Other sports: HOME | AWAY.
          return (
            <div className="grid grid-cols-1 gap-2 md:grid-cols-2">
              {renderPanel('home')}
              {renderPanel('away')}
            </div>
          );
        })()}

        {id && !getRemoteSession() && (
          <AnimationsPanel
            gameId={id}
            animationConfig={game.animationConfig}
            fire={fire}
            onConfigChange={load}
            disabled={disabled}
          />
        )}

        {id && !getRemoteSession() && <BroadcastOverlayPanel gameId={id} />}

        <p className="text-xs text-muted">
          Every action is an append-only event in the game log. Undo flags
          the last event as undone rather than deleting it.
        </p>
      </div>
    </AppShell>
  );
}

// ─── Animations panel ───────────────────────────────────────────────

const ANIMATION_OPTIONS: { value: string; label: string }[] = [
  { value: 'THREE_POINTER_MADE', label: 'Three Pointer' },
  { value: 'SCORING_RUN', label: 'Scoring Run' },
  { value: 'LEAD_CHANGE', label: 'Lead Change' },
  { value: 'TIE_GAME', label: 'Tie Game' },
  { value: 'PLAYER_MILESTONE', label: 'Player Milestone' },
  { value: 'FOUL_OUT', label: 'Foul Out' },
  { value: 'BUZZER_BEATER', label: 'Buzzer Beater' },
  { value: 'PERIOD_END', label: 'End of Period' },
  { value: 'GAME_FINAL', label: 'Game Final' },
  { value: 'DUNK', label: 'Dunk' },
  { value: 'AND_ONE', label: 'And-One' },
  { value: 'TIMEOUT_TAKEOVER', label: 'Timeout Takeover' },
  { value: 'MAKE_NOISE', label: 'Make Some Noise' },
  { value: 'DEFENSE_CHANT', label: 'Defense Chant' },
  { value: 'CROWD_WAVE', label: 'Crowd Wave' },
  { value: 'SPONSOR_STINGER', label: 'Sponsor Stinger' },
];

function AnimationsPanel({
  gameId,
  animationConfig,
  fire,
  onConfigChange,
  disabled,
}: {
  gameId: string;
  animationConfig?: { enabled?: boolean; disabled?: string[] } | null;
  fire: (t: string, p?: Record<string, unknown>) => void;
  onConfigChange: () => void | Promise<void>;
  disabled: boolean;
}) {
  const [busy, setBusy] = useState(false);
  const [effect, setEffect] = useState<string>(ANIMATION_OPTIONS[0].value);
  const [side, setSide] = useState<'home' | 'away'>('home');

  const enabled = animationConfig?.enabled !== false;

  async function toggle() {
    setBusy(true);
    try {
      await api.setAnimationConfig(gameId, {
        enabled: !enabled,
        disabled: animationConfig?.disabled ?? [],
      });
      await onConfigChange();
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="card space-y-3">
      <div className="flex items-center justify-between gap-4">
        <div>
          <div className="text-xs uppercase tracking-wider text-muted">
            Scoreboard animations
          </div>
          <div className="mt-1 text-sm">
            Auto-play celebration effects on the game's displays (three-pointers,
            buzzer beaters, crowd prompts, sponsor stingers, …).
          </div>
        </div>
        <button
          type="button"
          onClick={toggle}
          disabled={busy || disabled}
          className={`btn ${enabled ? 'btn-secondary' : 'btn-primary'}`}
        >
          {enabled ? 'On' : 'Off'}
        </button>
      </div>

      {!enabled && (
        <p className="text-xs text-amber-300/80">
          Auto effects are off — they won't fire on the board. You can still
          play any effect manually below.
        </p>
      )}

      <div className="flex flex-wrap items-end gap-2">
        <label className="flex flex-col gap-1">
          <span className="text-xs uppercase tracking-wider text-muted">
            Play animation
          </span>
          <select
            className="input w-auto text-sm"
            value={effect}
            onChange={(e) => setEffect(e.target.value)}
          >
            {ANIMATION_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
        </label>
        <label className="flex flex-col gap-1">
          <span className="text-xs uppercase tracking-wider text-muted">
            Side
          </span>
          <select
            className="input w-auto text-sm"
            value={side}
            onChange={(e) => setSide(e.target.value as 'home' | 'away')}
          >
            <option value="home">Home</option>
            <option value="away">Away</option>
          </select>
        </label>
        <button
          type="button"
          className="btn btn-primary"
          disabled={disabled}
          onClick={() => fire('EFFECT_TRIGGER', { effect, side })}
        >
          Play
        </button>
      </div>
    </div>
  );
}

// ─── Broadcast overlay panel ────────────────────────────────────────

function BroadcastOverlayPanel({ gameId }: { gameId: string }) {
  const [hasToken, setHasToken] = useState<boolean>(false);
  const [createdAt, setCreatedAt] = useState<string | null>(null);
  const [rawToken, setRawToken] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [pos, setPos] = useState<
    'bottom-left' | 'bottom-right' | 'top-left' | 'top-right'
  >('bottom-left');

  useEffect(() => {
    api.overlayStatus(gameId).then((s) => {
      setHasToken(s.hasToken);
      setCreatedAt(s.createdAt);
    });
  }, [gameId]);

  async function generate() {
    setBusy(true);
    try {
      const res = await api.createOverlayToken(gameId);
      setRawToken(res.token);
      setHasToken(true);
      setCreatedAt(new Date().toISOString());
    } finally {
      setBusy(false);
    }
  }

  async function revoke() {
    if (!confirm('Revoke the overlay link? Any OBS source using it will go blank.')) return;
    setBusy(true);
    try {
      await api.revokeOverlayToken(gameId);
      setHasToken(false);
      setRawToken(null);
      setCreatedAt(null);
    } finally {
      setBusy(false);
    }
  }

  const displayOrigin =
    typeof window !== 'undefined'
      ? window.location.origin.replace('app.', 'display.')
      : '';
  const url = rawToken
    ? `${displayOrigin}/overlay/${rawToken}?pos=${pos}`
    : null;

  return (
    <div className="card space-y-3">
      <div className="flex items-center justify-between">
        <div>
          <div className="text-xs uppercase tracking-wider text-muted">
            Broadcast overlay
          </div>
          <div className="mt-1 text-sm">
            Drop this URL into OBS / Streamlabs / vMix as a browser source
            (1920×1080, transparent background) to embed a live score bug.
          </div>
        </div>
        <div className="flex items-center gap-2">
          {!hasToken && (
            <button
              type="button"
              onClick={generate}
              disabled={busy}
              className="btn btn-primary"
            >
              Generate URL
            </button>
          )}
          {hasToken && (
            <>
              <button
                type="button"
                onClick={generate}
                disabled={busy}
                className="btn btn-secondary"
              >
                Regenerate
              </button>
              <button
                type="button"
                onClick={revoke}
                disabled={busy}
                className="btn btn-ghost text-red-300"
              >
                Revoke
              </button>
            </>
          )}
        </div>
      </div>

      {hasToken && !rawToken && (
        <p className="text-xs text-muted">
          An overlay URL exists (created{' '}
          {createdAt
            ? new Date(createdAt).toLocaleString()
            : 'previously'}
          ). For security the raw URL is only shown right after generation
          — click <strong>Regenerate</strong> to get a new one (revokes the
          old one).
        </p>
      )}

      {url && (
        <>
          <div className="flex items-center gap-2">
            <span className="text-xs uppercase tracking-wider text-muted">
              Position
            </span>
            <select
              className="input w-auto text-sm"
              value={pos}
              onChange={(e) =>
                setPos(
                  e.target.value as
                    | 'bottom-left'
                    | 'bottom-right'
                    | 'top-left'
                    | 'top-right',
                )
              }
            >
              <option value="bottom-left">Bottom-left</option>
              <option value="bottom-right">Bottom-right</option>
              <option value="top-left">Top-left</option>
              <option value="top-right">Top-right</option>
            </select>
          </div>
          <div className="flex items-stretch gap-2">
            <input
              readOnly
              value={url}
              className="input flex-1 font-mono text-xs"
              onFocus={(e) => e.currentTarget.select()}
            />
            <button
              type="button"
              className="btn btn-primary"
              onClick={() => {
                navigator.clipboard.writeText(url);
              }}
            >
              Copy
            </button>
          </div>
          <p className="text-xs text-amber-300/80">
            ⚠ Save this URL now — for security it's not shown again on
            refresh.
          </p>
        </>
      )}
    </div>
  );
}

// ─── Halftime countdown banner ──────────────────────────────────────

function HalftimeBanner({ endsAt }: { endsAt: string | null }) {
  const [, force] = useState(0);
  useEffect(() => {
    if (!endsAt) return;
    const id = setInterval(() => force((x) => x + 1), 1000);
    return () => clearInterval(id);
  }, [endsAt]);

  let label = '';
  let expired = false;
  if (endsAt) {
    const remainingMs = new Date(endsAt).getTime() - serverNow();
    const remaining = Math.max(0, Math.floor(remainingMs / 1000));
    const m = Math.floor(remaining / 60);
    const s = remaining - m * 60;
    label = `${m}:${String(s).padStart(2, '0')}`;
    expired = remaining === 0;
  }

  return (
    <div
      className={`flex items-center justify-between gap-4 rounded-lg border px-5 py-3 ${
        expired
          ? 'border-red-400/60 bg-red-400/15 text-red-200'
          : 'border-amber-400/50 bg-amber-400/15 text-amber-200'
      }`}
    >
      <div className="text-sm font-bold uppercase tracking-[0.3em]">
        🏟 Halftime
      </div>
      <div className="text-right">
        <div className="text-[10px] uppercase tracking-wider opacity-80">
          {expired ? 'Time to play' : 'Countdown'}
        </div>
        <div className="font-mono text-3xl font-bold tabular-nums">
          {label || '—'}
        </div>
      </div>
    </div>
  );
}

// ─── Game events row (basketball cascade buttons) ───────────────────

// Vertical column of compound game-event buttons, placed between the
// HOME and AWAY team cards.
function GameEventsColumn({
  fire,
  disabled,
}: {
  fire: (t: string, p?: Record<string, unknown>) => void;
  disabled: boolean;
}) {
  return (
    <div className="card flex flex-col gap-2 lg:w-44">
      <div className="text-center text-[10px] uppercase tracking-wider text-muted">
        Game events
      </div>
      <EventBtn
        label="↺ Change poss."
        sub="flip + reset SC"
        onClick={() => fire('CHANGE_POSSESSION')}
        disabled={disabled}
      />
      <EventBtn
        label="Off. rebound"
        sub="partial SC reset"
        onClick={() => fire('OFFENSIVE_REBOUND')}
        disabled={disabled}
      />
      <EventBtn
        label="Def. rebound"
        sub="= change poss"
        onClick={() => fire('DEFENSIVE_REBOUND')}
        disabled={disabled}
      />
      <EventBtn
        label="OOB off Def"
        sub="off keeps, full SC"
        onClick={() => fire('OUT_OF_BOUNDS', { off: 'defense' })}
        disabled={disabled}
      />
      <EventBtn
        label="OOB off Off"
        sub="flip poss, no SC"
        onClick={() => fire('OUT_OF_BOUNDS', { off: 'offense' })}
        disabled={disabled}
      />
      <EventBtn
        label="Violation"
        sub="travel/double"
        onClick={() => fire('VIOLATION')}
        disabled={disabled}
      />
    </div>
  );
}

function EventBtn({
  label,
  sub,
  onClick,
  disabled,
}: {
  label: string;
  sub: string;
  onClick: () => void;
  disabled: boolean;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className="flex flex-col items-center rounded-md border border-border bg-surface px-2 py-2.5 transition hover:border-brand hover:bg-brand/10 disabled:cursor-not-allowed disabled:opacity-40"
    >
      <span className="text-sm font-bold">{label}</span>
      <span className="mt-0.5 text-[10px] uppercase tracking-wider text-muted">
        {sub}
      </span>
    </button>
  );
}

// ─── Lineup sizing per sport ────────────────────────────────────────

function lineupSizeFor(sportSlug: string): number {
  switch (sportSlug) {
    case 'basketball': return 5;
    case 'football':   return 11;
    case 'soccer':     return 11;
    default:           return 5;
  }
}

// ─── Player row (used inside ScoringPanel) ──────────────────────────

function formatMinutes(seconds: number): string {
  if (seconds <= 0) return '0:00';
  const m = Math.floor(seconds / 60);
  const s = Math.floor(seconds - m * 60);
  return `${m}:${String(s).padStart(2, '0')}`;
}

function useLiveSeconds(
  closedSec: number,
  openSince: string | null | undefined,
): number {
  const [, force] = useState(0);
  useEffect(() => {
    if (!openSince) return;
    const id = setInterval(() => force((x) => x + 1), 1000);
    return () => clearInterval(id);
  }, [openSince]);
  if (!openSince) return closedSec;
  const live = Math.floor(
    (serverNow() - new Date(openSince).getTime()) / 1000,
  );
  return closedSec + Math.max(0, live);
}

function PlayerRow({
  player,
  stats,
  stintOpenSince,
  foulOut,
  isBench,
  armed,
  subSelected,
  subTarget,
  scoringActions,
  showFoul,
  disabled,
  onTapName,
  onScore,
  onFoul,
}: {
  player: { id: string; name: string; jerseyNumber: string | null };
  stats?: Record<string, number>;
  stintOpenSince: string | null | undefined;
  foulOut?: boolean;
  isBench: boolean;
  armed: boolean;
  subSelected: boolean;
  subTarget?: boolean;
  scoringActions: ScoringAction[];
  showFoul: boolean;
  disabled: boolean;
  onTapName: () => void;
  onScore: (points: number, eventType: string) => void;
  onFoul: () => void;
}) {
  const pts = typeof stats?.pts === 'number' ? stats.pts : 0;
  const f = typeof stats?.f === 'number' ? stats.f : 0;
  const closedSec = typeof stats?.sec === 'number' ? stats.sec : 0;
  const liveSec = useLiveSeconds(closedSec, stintOpenSince);
  const buttonsDisabled = disabled || isBench;

  // Visual treatment per state
  let rowClass = 'flex items-stretch gap-1.5 rounded-md border transition';
  if (subSelected) {
    rowClass += ' border-amber-400 bg-amber-400/15';
  } else if (isBench && subTarget) {
    rowClass += ' border-emerald-400/60 bg-emerald-400/5 hover:bg-emerald-400/15 cursor-pointer';
  } else if (isBench) {
    rowClass += ' border-border bg-surface/40 opacity-60';
  } else if (armed) {
    rowClass += ' border-brand bg-brand/10';
  } else {
    rowClass += ' border-border bg-surface hover:border-brand/40';
  }

  return (
    <div className={rowClass}>
      {/* Name area — tap to sub or arm for secondary actions */}
      <button
        type="button"
        onClick={onTapName}
        disabled={disabled}
        className="flex flex-1 items-center gap-2 px-2 py-1.5 text-left disabled:opacity-50"
      >
        <span className="w-9 shrink-0 text-right font-mono text-sm font-bold text-muted">
          #{player.jerseyNumber || '-'}
        </span>
        <span className="min-w-0 flex-1 truncate text-sm font-semibold">
          {player.name.split(' ').slice(-1)[0]?.toUpperCase() ??
            player.name.toUpperCase()}
        </span>
        <span className="flex shrink-0 items-baseline gap-2 font-mono text-xs">
          {liveSec > 0 && (
            <span
              className={
                stintOpenSince ? 'text-sky-300' : 'text-muted'
              }
              title="Minutes played"
            >
              {formatMinutes(liveSec)}
            </span>
          )}
          {pts > 0 && <span className="text-emerald-400">{pts}p</span>}
          {f > 0 && (
            <span className={f >= 4 ? 'text-amber-300' : 'text-muted'}>
              {f}f
            </span>
          )}
        </span>
        {foulOut && (
          <span className="shrink-0 rounded bg-red-500 px-1.5 py-0.5 text-[10px] font-bold text-white">
            FOULED OUT
          </span>
        )}
        {subSelected && (
          <span className="shrink-0 rounded bg-amber-400 px-1.5 py-0.5 text-[10px] font-bold text-black">
            OUT?
          </span>
        )}
        {isBench && subTarget && (
          <span className="shrink-0 rounded bg-emerald-400 px-1.5 py-0.5 text-[10px] font-bold text-black">
            ↑ IN
          </span>
        )}
      </button>

      {/* Inline action buttons */}
      <div className="flex shrink-0 items-center gap-0.5 pr-1">
        {/* -1 correction */}
        <RowBtn
          onClick={() => onScore(-1, 'SCORE_ADJUST')}
          disabled={buttonsDisabled}
          tone="warn"
        >
          −1
        </RowBtn>
        {scoringActions
          .filter((a) => a.points > 0)
          .map((a) => (
            <RowBtn
              key={a.eventType}
              onClick={() => onScore(a.points, a.eventType)}
              disabled={buttonsDisabled}
              tone="score"
              title={a.sub}
            >
              {a.label}
            </RowBtn>
          ))}
        {showFoul && (
          <RowBtn
            onClick={onFoul}
            disabled={buttonsDisabled}
            tone="foul"
          >
            F
          </RowBtn>
        )}
      </div>
    </div>
  );
}

function RowBtn({
  children,
  onClick,
  disabled,
  tone,
  title,
}: {
  children: React.ReactNode;
  onClick: () => void;
  disabled: boolean;
  tone: 'score' | 'foul' | 'warn';
  title?: string;
}) {
  const styles =
    tone === 'foul'
      ? 'border-amber-400/50 text-amber-300 hover:bg-amber-400/20 active:bg-amber-400/30'
      : tone === 'warn'
      ? 'border-red-400/50 text-red-300 hover:bg-red-400/20 active:bg-red-400/30'
      : 'border-emerald-400/50 text-emerald-300 hover:bg-emerald-400/20 active:bg-emerald-400/30';
  return (
    <button
      type="button"
      onClick={(e) => {
        e.stopPropagation();
        onClick();
      }}
      disabled={disabled}
      title={title}
      className={`min-w-[3.25rem] rounded-md border-2 px-3 py-2.5 text-lg font-bold transition disabled:cursor-not-allowed disabled:border-border disabled:text-muted/30 disabled:hover:bg-transparent ${styles}`}
    >
      {children}
    </button>
  );
}

// ─── Sport-specific action palettes ─────────────────────────────────

function scoringActionsFor(sportSlug: string): ScoringAction[] {
  switch (sportSlug) {
    case 'basketball':
      return [
        { label: '+1', sub: 'Free throw',   eventType: 'FREE_THROW',    points: 1 },
        { label: '+2', sub: 'Field goal',   eventType: 'FIELD_GOAL',    points: 2 },
        { label: '+3', sub: 'Three',        eventType: 'THREE_POINTER', points: 3 },
      ];
    case 'football':
      return [
        { label: '+6', sub: 'TD',           eventType: 'TOUCHDOWN',            points: 6 },
        { label: '+1', sub: 'XP',           eventType: 'EXTRA_POINT',          points: 1 },
        { label: '+2', sub: '2-pt',         eventType: 'TWO_POINT_CONVERSION', points: 2 },
        { label: '+3', sub: 'FG',           eventType: 'FIELD_GOAL',           points: 3 },
        { label: '+2', sub: 'Safety',       eventType: 'SAFETY',               points: 2 },
      ];
    case 'soccer':
      return [{ label: '+1', sub: 'Goal',   eventType: 'GOAL',         points: 1 }];
    default:
      return [];
  }
}

function secondaryActionsFor(sportSlug: string): SecondaryAction[] {
  switch (sportSlug) {
    case 'soccer':
      return [
        { label: 'Yellow card', eventType: 'YELLOW_CARD' },
        { label: 'Red card',    eventType: 'RED_CARD' },
        { label: 'Shot',        eventType: 'SHOT' },
        { label: 'Corner',      eventType: 'CORNER' },
      ];
    default:
      return [];
  }
}

// ─── Football play clock ────────────────────────────────────────────

function PlayClockControl({
  state,
  fire,
  disabled,
}: {
  state: ProjectedGameState;
  fire: (t: string, p?: Record<string, unknown>) => void;
  disabled: boolean;
}) {
  const ss = (state.sportState ?? {}) as Record<string, unknown>;
  const raw = typeof ss.playClock === 'number' ? (ss.playClock as number) : 40;
  const running = ss.playClockRunning === true;
  const startedAt =
    typeof ss.playClockStartedAt === 'string'
      ? (ss.playClockStartedAt as string)
      : null;
  const live = useLiveShotClock(raw, running, startedAt);
  const def = typeof ss.playClockDefault === 'number' ? (ss.playClockDefault as number) : 40;

  return (
    <div className="card flex flex-wrap items-center justify-between gap-4">
      <div>
        <div className="text-xs uppercase tracking-wider text-muted">
          Play clock
        </div>
        <div
          className={`mt-1 font-mono text-4xl font-bold ${
            live <= 5 ? 'text-red-400' : running ? 'text-amber-300' : ''
          }`}
        >
          {live}
        </div>
      </div>
      <div className="flex flex-wrap items-center gap-2">
        {running ? (
          <button
            className="btn btn-secondary"
            onClick={() => fire('PLAY_CLOCK_STOP')}
            disabled={disabled}
          >
            ⏸ Stop
          </button>
        ) : (
          <button
            className="btn btn-secondary"
            onClick={() => fire('PLAY_CLOCK_START')}
            disabled={disabled}
          >
            ▶ Start
          </button>
        )}
        <button
          className="btn btn-secondary"
          onClick={() => fire('PLAY_CLOCK_RESET', { value: def })}
          disabled={disabled}
        >
          Reset {def}
        </button>
        <button
          className="btn btn-secondary"
          onClick={() => fire('PLAY_CLOCK_RESET', { value: 25 })}
          disabled={disabled}
        >
          Reset 25
        </button>
      </div>
    </div>
  );
}

function DownDistanceControl({
  state,
  fire,
  disabled,
}: {
  state: ProjectedGameState;
  fire: (t: string, p?: Record<string, unknown>) => void;
  disabled: boolean;
}) {
  const ss = (state.sportState ?? {}) as Record<string, unknown>;
  const down = typeof ss.down === 'number' ? (ss.down as number) : 1;
  const distance = typeof ss.distance === 'number' ? (ss.distance as number) : 10;
  const yardLine = typeof ss.yardLine === 'number' ? (ss.yardLine as number) : 25;
  const yardSide = ss.yardSide === 'home' || ss.yardSide === 'away' ? ss.yardSide : 'home';

  return (
    <div className="card flex flex-wrap items-center justify-between gap-4">
      <div className="flex items-center gap-8">
        <div>
          <div className="text-xs uppercase tracking-wider text-muted">Down</div>
          <div className="mt-1 font-mono text-4xl font-bold">
            {down}
            <span className="ml-1 text-xl text-muted">
              {down === 1 ? 'st' : down === 2 ? 'nd' : down === 3 ? 'rd' : 'th'}
            </span>
          </div>
        </div>
        <div>
          <div className="text-xs uppercase tracking-wider text-muted">
            And
          </div>
          <div className="mt-1 font-mono text-4xl font-bold">{distance}</div>
        </div>
        <div>
          <div className="text-xs uppercase tracking-wider text-muted">
            Ball on
          </div>
          <div className="mt-1 font-mono text-4xl font-bold">
            {(yardSide as string).toUpperCase()} {yardLine}
          </div>
        </div>
      </div>
      <div className="flex flex-wrap items-center gap-2">
        <button
          className="btn btn-secondary"
          onClick={() => fire('FIRST_DOWN')}
          disabled={disabled}
        >
          1st down
        </button>
        <button
          className="btn btn-secondary"
          onClick={() => fire('DOWN_ADVANCE')}
          disabled={disabled}
        >
          Next down
        </button>
        <button
          className="btn btn-ghost"
          onClick={() => {
            const d = prompt('Down (1-4)', String(down));
            if (d === null) return;
            const dist = prompt('Yards to go', String(distance));
            if (dist === null) return;
            const yl = prompt('Yard line (0-50)', String(yardLine));
            if (yl === null) return;
            const sd = prompt('Side (home/away)', String(yardSide));
            if (sd === null) return;
            fire('BALL_SPOT', {
              down: parseInt(d, 10) || 1,
              distance: parseInt(dist, 10) || 10,
              yardLine: parseInt(yl, 10) || 25,
              yardSide: sd === 'away' ? 'away' : 'home',
            });
          }}
          disabled={disabled}
        >
          Spot ball…
        </button>
      </div>
    </div>
  );
}

function StoppageControl({
  state,
  fire,
  disabled,
}: {
  state: ProjectedGameState;
  fire: (t: string, p?: Record<string, unknown>) => void;
  disabled: boolean;
}) {
  const ss = (state.sportState ?? {}) as Record<string, unknown>;
  const stoppage = typeof ss.stoppageMinutes === 'number' ? (ss.stoppageMinutes as number) : 0;

  return (
    <div className="card flex flex-wrap items-center justify-between gap-4">
      <div>
        <div className="text-xs uppercase tracking-wider text-muted">
          Stoppage (added time)
        </div>
        <div className="mt-1 font-mono text-4xl font-bold">+{stoppage}'</div>
      </div>
      <div className="flex flex-wrap items-center gap-2">
        {[0, 1, 2, 3, 4, 5].map((n) => (
          <button
            key={n}
            className={`btn ${
              stoppage === n ? 'btn-primary' : 'btn-secondary'
            }`}
            onClick={() => fire('STOPPAGE_SET', { minutes: n })}
            disabled={disabled}
          >
            +{n}'
          </button>
        ))}
      </div>
    </div>
  );
}

// ─── Clock control ────────────────────────────────────────────────────

function useLiveClock(
  m: number,
  s: number,
  running: boolean,
  startedAt: string | null | undefined,
): { m: number; s: number } {
  const [tick, setTick] = useState(0);
  useEffect(() => {
    if (!running) return;
    const id = setInterval(() => setTick((x) => x + 1), 250);
    return () => clearInterval(id);
  }, [running]);
  // Tick is read to force re-render; calculate every time:
  void tick;

  if (!running || !startedAt) return { m, s };
  const elapsedSec = Math.max(
    0,
    Math.floor((serverNow() - new Date(startedAt).getTime()) / 1000),
  );
  const total = Math.max(0, m * 60 + s - elapsedSec);
  return { m: Math.floor(total / 60), s: total % 60 };
}

function ClockControl({
  state,
  fire,
  disabled,
}: {
  state: ProjectedGameState;
  fire: (t: string, p?: Record<string, unknown>) => void;
  disabled: boolean;
}) {
  const live = useLiveClock(
    state.clock.m,
    state.clock.s,
    state.clock.running,
    state.clock.startedAt,
  );
  const [editing, setEditing] = useState(false);
  const [draftM, setDraftM] = useState(state.clock.m);
  const [draftS, setDraftS] = useState(state.clock.s);

  function openEdit() {
    setDraftM(state.clock.m);
    setDraftS(state.clock.s);
    setEditing(true);
  }
  function applyEdit() {
    fire('CLOCK_SET', {
      m: Math.max(0, Math.floor(draftM)),
      s: Math.max(0, Math.min(59, Math.floor(draftS))),
    });
    setEditing(false);
  }

  return (
    <div className="card space-y-3">
      {state.intermission === 'halftime' && (
        <HalftimeBanner endsAt={state.intermissionEndsAt} />
      )}
      {state.intermission === 'period_break' && (
        <div className="rounded-lg border border-white/10 bg-white/5 px-4 py-2 text-center text-xs uppercase tracking-[0.3em] text-muted">
          Period break — start clock to begin period {state.period}
        </div>
      )}

      <div className="text-center">
        <div className="flex items-start justify-center gap-8">
          <div>
            <div className="text-xs uppercase tracking-wider text-muted">
              Game clock
            </div>
            {!editing ? (
              <button
                type="button"
                onClick={openEdit}
                disabled={disabled}
                className={`mt-1 font-mono text-4xl font-bold disabled:opacity-50 ${
                  state.clock.running ? 'text-emerald-300' : ''
                }`}
                title="Click to set time"
              >
                {live.m}:{String(live.s).padStart(2, '0')}
              </button>
            ) : (
              <div className="mt-1 flex items-baseline gap-1 font-mono text-4xl font-bold">
                <input
                  type="number"
                  min={0}
                  value={draftM}
                  onChange={(e) =>
                    setDraftM(parseInt(e.target.value, 10) || 0)
                  }
                  className="w-20 rounded border border-border bg-surface px-2 text-center"
                  autoFocus
                />
                <span>:</span>
                <input
                  type="number"
                  min={0}
                  max={59}
                  value={String(draftS).padStart(2, '0')}
                  onChange={(e) =>
                    setDraftS(parseInt(e.target.value, 10) || 0)
                  }
                  className="w-20 rounded border border-border bg-surface px-2 text-center"
                />
              </div>
            )}
          </div>
          <div>
            <div className="text-xs uppercase tracking-wider text-muted">
              Period
            </div>
            <div className="mt-1 font-mono text-4xl font-bold">
              {state.period}
            </div>
          </div>
        </div>
        <div className="mt-2 flex flex-wrap items-center justify-center gap-2">
          {editing ? (
            <>
              <button
                className="btn btn-primary"
                onClick={applyEdit}
                disabled={disabled}
              >
                Apply
              </button>
              <button
                className="btn btn-ghost"
                onClick={() => setEditing(false)}
              >
                Cancel
              </button>
            </>
          ) : (
            <>
              {state.clock.running ? (
                <button
                  className="btn btn-primary"
                  onClick={() => fire('CLOCK_STOP')}
                  disabled={disabled}
                >
                  ⏸ Stop clock
                </button>
              ) : (
                <button
                  className="btn btn-primary"
                  onClick={() => fire('CLOCK_START')}
                  disabled={disabled}
                >
                  ▶ {state.intermission === 'halftime'
                    ? 'Start 2nd half'
                    : state.intermission === 'period_break'
                    ? `Start period ${state.period}`
                    : 'Start clock'}
                </button>
              )}
              <button
                className="btn btn-secondary"
                onClick={openEdit}
                disabled={disabled}
              >
                Set time…
              </button>
              <button
                className="btn btn-secondary"
                onClick={() => fire('PERIOD_END')}
                disabled={disabled}
              >
                End period →
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── Shot clock ──────────────────────────────────────────────────────

function useLiveShotClock(
  value: number,
  running: boolean,
  startedAt: string | null | undefined,
): number {
  const [tick, setTick] = useState(0);
  useEffect(() => {
    if (!running) return;
    const id = setInterval(() => setTick((x) => x + 1), 250);
    return () => clearInterval(id);
  }, [running]);
  void tick;

  if (!running || !startedAt) return value;
  const elapsedSec = Math.max(
    0,
    Math.floor((serverNow() - new Date(startedAt).getTime()) / 1000),
  );
  return Math.max(0, value - elapsedSec);
}

function ShotClockControl({
  state,
  fire,
  disabled,
}: {
  state: ProjectedGameState;
  fire: (t: string, p?: Record<string, unknown>) => void;
  disabled: boolean;
}) {
  const ss = (state.sportState ?? {}) as Record<string, unknown>;
  const raw = typeof ss.shotClock === 'number' ? ss.shotClock : 24;
  const running =
    typeof ss.shotClockRunning === 'boolean' ? ss.shotClockRunning : false;
  const startedAt =
    typeof ss.shotClockStartedAt === 'string' ? ss.shotClockStartedAt : null;
  const live = useLiveShotClock(raw, running, startedAt);

  return (
    <div className="card text-center">
      <div className="text-xs uppercase tracking-wider text-muted">
        Shot clock
      </div>
      <div
        className={`mt-1 font-mono text-4xl font-bold ${
          live <= 5 ? 'text-red-400' : running ? 'text-amber-300' : ''
        }`}
      >
        {live}
      </div>
      <div className="mt-2 flex flex-wrap items-center justify-center gap-2">
        {running ? (
          <button
            className="btn btn-secondary"
            onClick={() => fire('SHOT_CLOCK_STOP')}
            disabled={disabled}
          >
            ⏸ Stop
          </button>
        ) : (
          <button
            className="btn btn-secondary"
            onClick={() => fire('SHOT_CLOCK_START')}
            disabled={disabled}
          >
            ▶ Start
          </button>
        )}
        <button
          className="btn btn-secondary"
          onClick={() => fire('SHOT_CLOCK_RESET', { value: 24 })}
          disabled={disabled}
        >
          Reset 24
        </button>
        <button
          className="btn btn-secondary"
          onClick={() => fire('SHOT_CLOCK_RESET', { value: 14 })}
          disabled={disabled}
        >
          Reset 14
        </button>
        <button
          className="btn btn-ghost"
          onClick={() => {
            const v = prompt('Set shot clock seconds', String(raw));
            if (v === null) return;
            fire('SHOT_CLOCK_SET', { value: parseInt(v, 10) || 24 });
          }}
          disabled={disabled}
        >
          Set…
        </button>
      </div>
    </div>
  );
}

// ─── Possession ──────────────────────────────────────────────────────

function PossessionControl({
  state,
  fire,
  disabled,
}: {
  state: ProjectedGameState;
  fire: (t: string, p?: Record<string, unknown>) => void;
  disabled: boolean;
}) {
  const side = state.possession;
  return (
    <div className="card flex items-center justify-between gap-4">
      <div>
        <div className="text-xs uppercase tracking-wider text-muted">
          Possession
        </div>
        <div className="mt-1 text-xl font-semibold">
          {side === 'home'
            ? 'HOME ◀'
            : side === 'away'
            ? '▶ AWAY'
            : '—'}
        </div>
      </div>
      <div className="flex items-center gap-2">
        <button
          className={`btn ${side === 'home' ? 'btn-primary' : 'btn-secondary'}`}
          onClick={() => fire('POSSESSION', { side: 'home' })}
          disabled={disabled}
        >
          ◀ Home
        </button>
        <button
          className="btn btn-ghost"
          onClick={() => fire('POSSESSION', { side: null })}
          disabled={disabled}
        >
          Clear
        </button>
        <button
          className={`btn ${side === 'away' ? 'btn-primary' : 'btn-secondary'}`}
          onClick={() => fire('POSSESSION', { side: 'away' })}
          disabled={disabled}
        >
          Away ▶
        </button>
      </div>
    </div>
  );
}

// ─── Scoring panel ───────────────────────────────────────────────────

interface TeamWithPlayers {
  name: string;
  abbreviation: string | null;
  colorPrimary: string | null;
  players?: Array<{
    id: string;
    name: string;
    jerseyNumber: string | null;
    position: string | null;
  }>;
}

interface ScoringAction {
  label: string;
  sub: string;
  eventType: string;
  points: number;
}

interface SecondaryAction {
  label: string;
  eventType: string;
  payload?: Record<string, unknown>;
  /** Stat key to show count from team-side state ('fouls') or
   *  sportState ('cards.home.yellow'). Optional. */
  countFrom?: 'fouls' | 'timeouts' | { sportStatePath: string };
}

function ScoringPanel({
  label,
  team,
  side,
  score,
  fouls,
  timeouts,
  bonus,
  doubleBonus,
  playerStats,
  playerStints,
  foulOutPlayers,
  sportSlug,
  onCourt,
  lineupSize,
  showPossession,
  hasPossession,
  onPossession,
  onLineupSet,
  onSubstitute,
  scoringActions,
  secondaryActions,
  showFoulButton,
  showTimeoutButton,
  onScore,
  onFoul,
  onTimeout,
  onSecondary,
  disabled,
}: {
  label: string;
  team: TeamWithPlayers;
  side: 'home' | 'away';
  score: number;
  fouls: number;
  timeouts: number;
  bonus: boolean;
  doubleBonus?: boolean;
  playerStats?: Record<string, Record<string, number>>;
  playerStints?: Record<string, string>;
  foulOutPlayers: string[];
  sportSlug: string;
  onCourt: string[];
  lineupSize: number;
  showPossession?: boolean;
  hasPossession?: boolean;
  onPossession?: () => void;
  onLineupSet: (playerIds: string[]) => void;
  onSubstitute: (inPlayerId: string, outPlayerId: string) => void;
  scoringActions: ScoringAction[];
  secondaryActions?: SecondaryAction[];
  showFoulButton: boolean;
  showTimeoutButton: boolean;
  onScore: (pts: number, eventType: string, playerId?: string) => void;
  onFoul: (playerId?: string, foulType?: string) => void;
  onTimeout: () => void;
  onSecondary?: (action: SecondaryAction, playerId?: string) => void;
  disabled: boolean;
}) {
  void side;
  // For secondary-action attribution (cards/shots in soccer), the
  // operator can tap a player's stats column to "arm" them, then tap
  // a secondary button below.
  const [armedPlayerId, setArmedPlayerId] = useState<string | null>(null);
  // Sub-out state: when set, we're waiting for the operator to tap a
  // bench player to complete the swap.
  const [subOutId, setSubOutId] = useState<string | null>(null);
  // Current foul type — the F button on any row fires with this type.
  // Resets back to 'defensive' after each foul (most common case).
  const [foulType, setFoulType] = useState<
    'defensive' | 'offensive' | 'shooting' | 'technical'
  >('defensive');
  const foulOutSet = new Set(foulOutPlayers);
  const color = team.colorPrimary ?? '#3b82f6';
  const players = team.players ?? [];

  const onCourtSet = new Set(onCourt);
  const lineupReady = onCourt.length > 0;
  // When no lineup has been explicitly set, treat everyone as "on court"
  // so the operator can still record stats without configuring a lineup.
  const onCourtPlayers = lineupReady
    ? (onCourt
        .map((id) => players.find((p) => p.id === id))
        .filter(Boolean) as typeof players)
    : players;
  const benchPlayers = lineupReady
    ? players.filter((p) => !onCourtSet.has(p.id))
    : [];

  function handleRowTap(p: { id: string }, isBench: boolean) {
    if (!lineupReady) {
      // No lineup set yet — row taps just arm a player for secondary
      // actions (or clear).
      setArmedPlayerId(armedPlayerId === p.id ? null : p.id);
      return;
    }
    if (isBench) {
      // Bench tap completes a pending sub-out, otherwise no-op.
      if (subOutId) {
        onSubstitute(p.id, subOutId);
        setSubOutId(null);
      }
      return;
    }
    // On-court tap: toggle sub-out mode for this player.
    setSubOutId(subOutId === p.id ? null : p.id);
    setArmedPlayerId(null);
  }

  return (
    <div className="card relative">
      {/* Possession — noticeable button in the inner-top corner nearest
          the game-events column (home: top-right, away: top-left). */}
      {showPossession && (
        <button
          type="button"
          onClick={onPossession}
          disabled={disabled}
          className={`absolute top-3 ${
            side === 'home' ? 'right-3' : 'left-3'
          } rounded-lg border-2 px-3 py-1.5 text-xs font-bold uppercase tracking-wider transition disabled:opacity-50 ${
            hasPossession
              ? 'border-amber-400 bg-amber-400/25 text-amber-300 shadow-[0_0_12px_rgba(251,191,36,0.4)]'
              : 'border-border bg-surface text-muted hover:border-amber-400/50 hover:text-text'
          }`}
          title="Set possession to this team"
        >
          {side === 'home' ? '◀ ' : ''}Poss{side === 'away' ? ' ▶' : ''}
        </button>
      )}

      {/* Centered team header */}
      <div className="flex flex-col items-center gap-1">
        <div className="flex items-center gap-2">
          <span className="h-3 w-3 rounded-full" style={{ background: color }} />
          <span className="text-xs uppercase tracking-wider text-muted">
            {label}
          </span>
          {(bonus || doubleBonus) && (
            <span
              className={`rounded-full border px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider ${
                doubleBonus
                  ? 'border-red-400/50 bg-red-400/15 text-red-300'
                  : 'border-amber-400/40 bg-amber-400/10 text-amber-300'
              }`}
            >
              {doubleBonus ? 'Double bonus' : 'Bonus'}
            </span>
          )}
        </div>
        <div className="max-w-full truncate text-xl font-bold">{team.name}</div>
        <div className="font-mono text-5xl font-bold">{score}</div>
      </div>
      <div className="mb-2" />

      {/* ─── Foul type picker (basketball only) ─── */}
      {showFoulButton && sportSlug === 'basketball' && (
        <div className="mb-3 flex items-center justify-between text-xs">
          <span className="uppercase tracking-wider text-muted">
            Next foul type
          </span>
          <div className="inline-flex overflow-hidden rounded-md border border-border">
            {(
              [
                ['defensive', 'Def'],
                ['offensive', 'Off'],
                ['shooting', 'Shoot'],
                ['technical', 'Tech'],
              ] as const
            ).map(([key, label]) => (
              <button
                key={key}
                type="button"
                onClick={() => setFoulType(key)}
                disabled={disabled}
                className={`px-2 py-1 text-[11px] font-bold transition disabled:opacity-50 ${
                  foulType === key
                    ? 'bg-amber-400/20 text-amber-300'
                    : 'bg-surface text-muted hover:text-text'
                }`}
              >
                {label}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* ─── Lineup status row ─── */}
      {players.length > 0 && (
        <div className="mb-2 flex items-center justify-between text-xs">
          <span className="uppercase tracking-wider text-muted">
            {lineupReady
              ? `On court (${onCourtPlayers.length}/${lineupSize})`
              : `Roster (${players.length})`}
          </span>
          <div className="flex items-center gap-2">
            {subOutId && (
              <span className="rounded-full bg-amber-400/20 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-amber-300">
                Tap a bench player to sub in
              </span>
            )}
            {!lineupReady ? (
              <button
                type="button"
                onClick={() => {
                  const seed = [...players]
                    .sort((a, b) => {
                      const aN = parseInt(a.jerseyNumber ?? '999', 10);
                      const bN = parseInt(b.jerseyNumber ?? '999', 10);
                      return aN - bN;
                    })
                    .slice(0, lineupSize)
                    .map((p) => p.id);
                  onLineupSet(seed);
                }}
                disabled={disabled}
                className="btn btn-primary text-xs"
              >
                Set starting {lineupSize}
              </button>
            ) : (
              <button
                type="button"
                onClick={() => {
                  // Clearing the on-court list re-enters "all roster, no
                  // lineup" mode so the operator can re-pick from scratch.
                  onLineupSet([]);
                  setSubOutId(null);
                }}
                disabled={disabled}
                className="btn btn-ghost text-xs"
              >
                Clear lineup
              </button>
            )}
          </div>
        </div>
      )}

      {/* ─── On-court rows ─── */}
      <div className="mb-1 space-y-1">
        {onCourtPlayers.map((p) => (
          <PlayerRow
            key={p.id}
            player={p}
            stats={playerStats?.[p.id]}
            stintOpenSince={playerStints?.[p.id] ?? null}
            foulOut={foulOutSet.has(p.id)}
            isBench={false}
            armed={armedPlayerId === p.id}
            subSelected={subOutId === p.id}
            scoringActions={scoringActions}
            showFoul={showFoulButton}
            disabled={disabled}
            onTapName={() => handleRowTap(p, false)}
            onScore={(pts, type) => onScore(pts, type, p.id)}
            onFoul={() => {
              onFoul(p.id, foulType);
              setFoulType('defensive');
            }}
          />
        ))}
      </div>

      {/* ─── Bench (compact chips — tap to sub in) ─── */}
      {benchPlayers.length > 0 && (
        <>
          <div className="mb-1 mt-2 text-[10px] uppercase tracking-wider text-muted/70">
            Bench ({benchPlayers.length}) — {subOutId ? 'tap to sub in' : 'tap to add'}
          </div>
          <div className="flex flex-wrap gap-1.5">
            {benchPlayers.map((p) => {
              const f = playerStats?.[p.id]?.f ?? 0;
              return (
                <button
                  key={p.id}
                  type="button"
                  onClick={() => handleRowTap(p, true)}
                  disabled={disabled}
                  className={`rounded-md border px-2 py-1 text-xs font-semibold transition disabled:opacity-50 ${
                    subOutId
                      ? 'border-emerald-400/60 bg-emerald-400/10 text-emerald-200 hover:bg-emerald-400/20'
                      : 'border-border bg-surface/50 text-muted hover:text-text'
                  }`}
                >
                  <span className="font-mono">#{p.jerseyNumber || '-'}</span>{' '}
                  {p.name.split(' ').slice(-1)[0]?.toUpperCase()}
                  {f >= 4 && <span className="ml-1 text-amber-300">{f}f</span>}
                </button>
              );
            })}
          </div>
        </>
      )}

      {/* ─── Team-level actions ─── */}
      {(showTimeoutButton || (secondaryActions?.length ?? 0) > 0) && (
        <div className="mt-4 flex flex-wrap gap-2">
          {showTimeoutButton && (
            <button
              onClick={onTimeout}
              disabled={disabled}
              className="btn btn-secondary flex-1"
            >
              Timeout ({timeouts})
            </button>
          )}
          {secondaryActions?.map((sa) => (
            <button
              key={sa.eventType + ':' + sa.label}
              onClick={() => onSecondary?.(sa, armedPlayerId ?? undefined)}
              disabled={disabled}
              className="btn btn-secondary flex-1"
              title={
                armedPlayerId
                  ? `Apply to ${players.find((p) => p.id === armedPlayerId)?.name}`
                  : 'Tap a player to attribute, or click for team-level'
              }
            >
              {sa.label}
            </button>
          ))}
        </div>
      )}
      {/* Team fouls counter (info-only — fouls fire from each row's F btn) */}
      {showFoulButton && (
        <div className="mt-2 text-right text-[10px] uppercase tracking-wider text-muted">
          Team fouls: {fouls}
        </div>
      )}
    </div>
  );
}

// ─── Box score (completed game, read-only) ──────────────────────────

function BoxScoreView({
  game,
  state,
}: {
  game: GameDetail;
  state: ProjectedGameState;
}) {
  const homeWon = state.home.score > state.away.score;
  const awayWon = state.away.score > state.home.score;

  return (
    <AppShell>
      <div className="space-y-6">
        <div>
          <Link to="/games" className="text-sm text-muted hover:text-text">
            ← Games
          </Link>
          <div className="mt-2 flex items-center gap-3">
            <span
              className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-0.5 text-xs font-semibold uppercase tracking-wider ${
                game.status === 'FINAL'
                  ? 'border-white/15 bg-white/5 text-muted'
                  : 'border-red-500/30 bg-red-500/10 text-red-300'
              }`}
            >
              {game.status === 'FINAL' ? 'Final' : 'Cancelled'}
            </span>
            <span className="text-sm text-muted">
              {game.sport.name} · {game.venue.name}
            </span>
          </div>
          <h1 className="mt-3 text-3xl font-bold tracking-tight">
            {game.homeTeam.name} vs {game.awayTeam.name}
          </h1>
        </div>

        {/* Final score row */}
        <div className="card flex items-center justify-between gap-8">
          <FinalScoreCol
            team={game.homeTeam}
            score={state.home.score}
            won={homeWon}
          />
          <div className="text-center">
            <div className="text-xs uppercase tracking-wider text-muted">
              Period {state.period}
            </div>
            <div className="mt-1 font-mono text-3xl font-bold text-muted">
              FINAL
            </div>
          </div>
          <FinalScoreCol
            team={game.awayTeam}
            score={state.away.score}
            won={awayWon}
          />
        </div>

        {/* Box score tables */}
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          <BoxScoreTeamTable
            label="HOME"
            team={game.homeTeam}
            playerStats={state.playerStats}
          />
          <BoxScoreTeamTable
            label="AWAY"
            team={game.awayTeam}
            playerStats={state.playerStats}
          />
        </div>

        {/* Event log */}
        <EventLog game={game} />

        <p className="text-xs text-muted">
          Game ended {game.endedAt ? new Date(game.endedAt).toLocaleString() : ''}.
          The full append-only event log is preserved — undone events are
          flagged but never deleted.
        </p>
      </div>
    </AppShell>
  );
}

function FinalScoreCol({
  team,
  score,
  won,
}: {
  team: { name: string; colorPrimary: string | null };
  score: number;
  won: boolean;
}) {
  const color = team.colorPrimary ?? '#3b82f6';
  return (
    <div className="flex flex-1 flex-col items-center gap-2">
      <div className="h-3 w-16 rounded-full" style={{ background: color }} />
      <div className="truncate text-xl font-semibold">{team.name}</div>
      <div
        className={`font-mono text-7xl font-bold ${
          won ? 'text-emerald-300' : 'text-text'
        }`}
      >
        {score}
      </div>
      {won && (
        <span className="rounded-full border border-emerald-400/30 bg-emerald-400/10 px-3 py-0.5 text-xs font-bold uppercase tracking-wider text-emerald-300">
          Winner
        </span>
      )}
    </div>
  );
}

function BoxScoreTeamTable({
  label,
  team,
  playerStats,
}: {
  label: string;
  team: GameDetail['homeTeam'];
  playerStats?: Record<string, Record<string, number>>;
}) {
  // Sort by points desc, then jersey #
  const rows = [...team.players]
    .map((p) => {
      const s = playerStats?.[p.id];
      return {
        ...p,
        pts: typeof s?.pts === 'number' ? s.pts : 0,
        f: typeof s?.f === 'number' ? s.f : 0,
      };
    })
    .sort(
      (a, b) =>
        b.pts - a.pts ||
        (a.jerseyNumber ?? '').localeCompare(b.jerseyNumber ?? ''),
    );

  return (
    <div className="overflow-hidden rounded-2xl border border-border">
      <div className="border-b border-border bg-surface px-4 py-2 text-xs uppercase tracking-wider text-muted">
        {label} · {team.name}
      </div>
      <table className="w-full divide-y divide-border text-sm">
        <thead className="bg-surface text-xs uppercase tracking-wider text-muted">
          <tr>
            <th className="w-12 px-3 py-2 text-left">#</th>
            <th className="px-3 py-2 text-left">Player</th>
            <th className="w-16 px-3 py-2 text-right">PTS</th>
            <th className="w-16 px-3 py-2 text-right">F</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-border bg-card">
          {rows.map((p) => (
            <tr key={p.id}>
              <td className="px-3 py-2 font-mono text-text">
                {p.jerseyNumber || '—'}
              </td>
              <td className="px-3 py-2 text-text">{p.name}</td>
              <td className="px-3 py-2 text-right font-mono">{p.pts}</td>
              <td
                className={`px-3 py-2 text-right font-mono ${
                  p.f >= 4 ? 'text-amber-300' : ''
                }`}
              >
                {p.f}
              </td>
            </tr>
          ))}
          {rows.length === 0 && (
            <tr>
              <td colSpan={4} className="px-3 py-3 text-center text-muted">
                No roster recorded
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
}

function EventLog({ game }: { game: GameDetail }) {
  const events = [...(game.events ?? [])]
    .filter((e: any) => !e.undone)
    .reverse(); // newest first
  if (events.length === 0) {
    return (
      <div className="card text-center text-muted">No events recorded.</div>
    );
  }
  return (
    <details className="card" open={false}>
      <summary className="cursor-pointer font-semibold">
        Event log ({events.length})
      </summary>
      <ol className="mt-4 max-h-96 space-y-1 overflow-y-auto font-mono text-xs">
        {events.map((e: any) => {
          const payload = (e.payload ?? {}) as Record<string, unknown>;
          const side = payload.side ? String(payload.side) : '';
          const extras = Object.entries(payload)
            .filter(([k]) => k !== 'side')
            .map(([k, v]) => `${k}=${String(v)}`)
            .join(' ');
          return (
            <li key={e.id} className="text-muted">
              <span className="mr-2 text-white/30">#{String(e.sequence)}</span>
              <span className="mr-2 text-text">{e.eventType}</span>
              {side && <span className="mr-2 text-brand-light">{side}</span>}
              {extras && <span className="text-muted/70">{extras}</span>}
            </li>
          );
        })}
      </ol>
    </details>
  );
}
